import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ViewStorePopupComponent } from '../../shared/popups/view-store-popup/view-store-popup.component';
import { BrowserStorageService } from '../../shared/services/common/browser-storage.service';
import { ApiCallsService } from '../../shared/services/network/api-calls.service';
import { UserService } from '../../shared/services/user.service';
import { desiredOrder } from '../../utils/constants';
import { api } from '../../utils/endpoints';

@Component({
  selector: 'app-shop-gear',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  templateUrl: './shop-gear.component.html',
  styleUrl: './shop-gear.component.scss',
})
export class ShopGearComponent implements OnInit {
  private _storageService = inject(BrowserStorageService);
  private _userService = inject(UserService);
  private _ngbModal = inject(NgbModal);
  private _apiService = inject(ApiCallsService);

  userDetails: any;
  loading = true;
  storeProducts: GearProduct[] = [];
  private activeImageIndex: Record<string, number> = {};
  private readonly defaultImage = 'assets/images/home/hat.png';
  private readonly levelLabelMap: Record<string, string> = {
    level1: 'LEVEL 1',
    level2: 'LEVEL 2',
    level3: 'LEVEL 3',
    level4: 'LEVEL 4',
    level5: 'LEVEL 5',
    level6: 'LEVEL 6',
  };
  private readonly levelLabelFallback = 'LIMITED EDITION';
  private readonly numericLevelOrder: string[] = ['level1', 'level2', 'level3', 'level4', 'level5', 'level6'];
  private readonly legacyLevelTagMap: Record<string, string> = {
    diamond: 'level1',
    'double-diamond': 'level2',
    'triple-diamond': 'level3',
    xtream: 'level4',
    'xx-xtream': 'level5',
    joker: 'level5',
    ace: 'level6',
    'no level': '',
    'no-level': '',
  };
  private readonly fallbackProducts: GearProduct[] = [
    {
      id: 'level1',
      title: 'SIGNATURE DIAMOND HAT 123',
      levelLabel: 'LEVEL 1',
      price: 69,
      levelTag: 'level1',
      image: 'assets/images/home/hat.png',
      images: ['assets/images/home/hat.png'],
      onlineUrl: 'https://store.xdgesports.com',
      currencyCode: 'USD',
      isActive: false,
    },
    {
      id: 'level2',
      title: 'SIGNATURE DOUBLE DIAMOND HAT 123',
      levelLabel: 'LEVEL 2',
      price: 69,
      levelTag: 'level2',
      image: 'assets/images/home/hat.png',
      images: ['assets/images/home/hat.png'],
      onlineUrl: 'https://store.xdgesports.com',
      currencyCode: 'USD',
      isActive: true,
    },
    {
      id: 'level3',
      title: 'SIGNATURE TRIPLE DIAMOND HAT 123',
      levelLabel: 'LEVEL 3',
      price: 69,
      levelTag: 'level3',
      image: 'assets/images/home/hat.png',
      images: ['assets/images/home/hat.png'],
      onlineUrl: 'https://store.xdgesports.com',
      currencyCode: 'USD',
      isActive: false,
    },
    {
      id: 'level4',
      title: 'SIGNATURE XTREME HAT 123',
      levelLabel: 'LEVEL 4',
      price: 69,
      levelTag: 'level4',
      image: 'assets/images/home/hat.png',
      images: ['assets/images/home/hat.png'],
      onlineUrl: 'https://store.xdgesports.com',
      currencyCode: 'USD',
      isActive: false,
    },
  ];

  ngOnInit(): void {
    this._userService.user.subscribe((user: any) => {
      if (user) {
        this.userDetails = user;
      }
    });

    const userdata = this._storageService.getItem('userDetail');
    if (userdata) {
      try {
        this.userDetails = JSON.parse(userdata);
      } catch (e) {
        this.userDetails = userdata;
      }
    }

    if (this._storageService.getBrowser()) {
      this.getProductsFromShopify();
    } else {
      this.useFallbackProducts();
    }
  }

  getProductsFromShopify() {
    this._apiService.get(api._products, this._getRequest()).subscribe({
      next: (res: any) => {
        const edges = res?.data?.products?.edges || [];
        if (edges.length) {
          this.transformToStoreProducts(edges);
          this.loading = false;
          return;
        }
        this.useFallbackProducts();
      },
      error: (err: any) => {
        console.error('Failed to load products from Shopify API, using static defaults.', err);
        this.useFallbackProducts();
      },
    });
  }

  private _getRequest(): Record<string, number> {
    return { limit: 100 };
  }

  private useFallbackProducts() {
    const fallbackCopy = this.fallbackProducts.map((product) => ({ ...product }));
    this.storeProducts = fallbackCopy;
    this.initializeCarouselState(this.storeProducts);
    this.applyActiveState(null);
    this.loading = false;
  }

  private transformToStoreProducts(edges: any[]): void {
    const formatted = edges
      .map((edge) => this.mapEdgeToProduct(edge))
      .filter((product): product is GearProduct => !!product)
      .sort((a, b) => this.getLevelSortIndex(a.levelTag) - this.getLevelSortIndex(b.levelTag));

    const highlighted: GearProduct[] = formatted.slice(0, 4);

    if (!highlighted.length) {
      this.useFallbackProducts();
      return;
    }

    // Highlight Level 2 (double diamond) card if present, else highlight second card
    const level2Index = highlighted.findIndex((product) => product.levelTag === 'level2');
    const highlightIndex = level2Index !== -1 ? level2Index : highlighted.length > 1 ? 1 : 0;

    this.storeProducts = highlighted;
    this.initializeCarouselState(this.storeProducts);
    this.applyActiveState(null);
  }

  private mapEdgeToProduct(edge: any): GearProduct | null {
    const node = edge?.node;
    if (!node) {
      return null;
    }

    const tags: string[] = (node.tags || []).map((tag: string) => tag.toLowerCase());
    const levelTag = this.resolveLevelTag(tags);
    const images = this.collectProductImages(node);
    const image = images[0] || this.defaultImage;
    const rawPrice = parseFloat(node.priceRange?.minVariantPrice?.amount || '69');
    const currencyCode = node.priceRange?.minVariantPrice?.currencyCode || 'USD';

    return {
      id: node.id,
      title: node.title,
      levelLabel: this.levelLabelMap[levelTag] || this.levelLabelFallback,
      price: rawPrice,
      levelTag,
      image,
      images,
      onlineUrl: node.onlineStorePreviewUrl,
      currencyCode,
      isActive: false,
    };
  }

  buyProduct(product: any, event: Event) {
    event.stopPropagation();
    if (!this.userDetails) {
      this.onExploreClick(event);
      return;
    }

    const targetUrl = product?.onlineUrl || 'https://store.xdgesports.com';
    if (this._storageService.getWindow()) {
      window.open(targetUrl, '_blank');
    }
  }

  onExploreClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    let modalRef = this._ngbModal.open(ViewStorePopupComponent, {
      scrollable: false,
      backdrop: 'static',
      keyboard: false,
      size: 'filterPopup',
      centered: true,
    });
    modalRef.componentInstance.entity = 'XDGE Store';
  }

  trackByLevelTag(_: number, product: GearProduct) {
    return product.id || product.levelTag || product.title;
  }

  hasMultipleImages(product: GearProduct): boolean {
    return this.getProductImages(product).length > 1;
  }

  getActiveImage(product: GearProduct): string {
    const images = this.getProductImages(product);
    const key = this.getProductKey(product);
    const activeIndex = this.activeImageIndex[key] ?? 0;
    return images[activeIndex] || images[0];
  }

  isActiveCarouselImage(product: GearProduct, index: number): boolean {
    const key = this.getProductKey(product);
    return (this.activeImageIndex[key] ?? 0) === index;
  }

  onNextImage(product: GearProduct, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.updateImageIndex(product, 1);
  }

  onPrevImage(product: GearProduct, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.updateImageIndex(product, -1);
  }

  onSelectImage(product: GearProduct, index: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.setImageIndex(product, index);
  }

  onProductHover(product: GearProduct) {
    if (product?.id) {
      this.applyActiveState(product.id);
    }
  }

  onProductLeave(event: MouseEvent) {
    const nextTarget = event.relatedTarget as HTMLElement | null;
    if (nextTarget && nextTarget.closest('.product-card')) {
      return;
    }
    this.applyActiveState(null);
  }

  private applyActiveState(productId: string | null) {
    this.storeProducts = this.storeProducts.map((product) => ({
      ...product,
      isActive: productId ? product.id === productId : false,
    }));
  }

  private resolveLevelTag(tags: string[]): string {
    const numericMatch = this.numericLevelOrder.find((tag) => tags.includes(tag));
    if (numericMatch) {
      return numericMatch;
    }

    const legacyMatch = desiredOrder.find((tag) => tags.includes(tag));
    return legacyMatch ? this.legacyLevelTagMap[legacyMatch] || '' : '';
  }

  private getLevelSortIndex(levelTag: string): number {
    const numericIndex = this.numericLevelOrder.indexOf(levelTag);
    if (numericIndex !== -1) {
      return numericIndex;
    }
    const legacyIndex = desiredOrder.indexOf(levelTag);
    return legacyIndex !== -1 ? this.numericLevelOrder.length + legacyIndex : Number.MAX_SAFE_INTEGER;
  }

  private initializeCarouselState(products: GearProduct[]) {
    const nextState: Record<string, number> = { ...this.activeImageIndex };
    products.forEach((product) => {
      const key = this.getProductKey(product);
      const images = this.getProductImages(product);
      const currentIndex = nextState[key] ?? 0;
      nextState[key] = currentIndex >= images.length ? 0 : currentIndex;
    });
    this.activeImageIndex = nextState;
  }

  private collectProductImages(node: any): string[] {
    const urls = new Set<string>();
    const mediaNodes = node?.media?.nodes || [];

    mediaNodes.forEach((mediaNode: any) => {
      const imageUrl = mediaNode?.preview?.image?.url || mediaNode?.image?.url || mediaNode?.url;
      if (imageUrl) {
        urls.add(imageUrl);
      }
    });

    const imageEdges = node?.images?.edges || [];
    imageEdges.forEach((edge: any) => {
      const imageUrl = edge?.node?.url || edge?.node?.transformedSrc || edge?.node?.previewImage?.url;
      if (imageUrl) {
        urls.add(imageUrl);
      }
    });

    if (!urls.size) {
      urls.add(this.defaultImage);
    }

    return Array.from(urls);
  }

  private updateImageIndex(product: GearProduct, delta: number) {
    const images = this.getProductImages(product);
    if (images.length <= 1) {
      return;
    }
    const key = this.getProductKey(product);
    const total = images.length;
    const current = this.activeImageIndex[key] ?? 0;
    const next = (current + delta + total) % total;
    this.activeImageIndex = { ...this.activeImageIndex, [key]: next };
  }

  private setImageIndex(product: GearProduct, targetIndex: number) {
    const images = this.getProductImages(product);
    if (targetIndex < 0 || targetIndex >= images.length) {
      return;
    }
    const key = this.getProductKey(product);
    this.activeImageIndex = { ...this.activeImageIndex, [key]: targetIndex };
  }

  private getProductKey(product: GearProduct): string {
    return product.id || product.levelTag || product.title;
  }

  getProductImages(product: GearProduct): string[] {
    if (product.images?.length) {
      return product.images;
    }
    return product.image ? [product.image] : [this.defaultImage];
  }
}

interface GearProduct {
  id: string;
  title: string;
  levelLabel: string;
  price: number;
  levelTag: string;
  image: string;
  images: string[];
  onlineUrl: string;
  currencyCode: string;
  isActive: boolean;
}
